import { castDraft, current, isDraft, original, WritableDraft } from 'immer';
import { deepEqual, DistributiveOmit, get, isObject, isPrimitive, Primitive } from '@direct-frontend/stdlib';
import { UnexpectedError } from '@adv-frontend/error';
import { CalculatedFieldStateProperties, FieldState, FormSliceState } from '../types';
import { BaseFields } from '../../fields/types';
import { BaseValidationError } from '../../types';
import { getFieldPathById } from './getFieldPathByFieldId';
import { getFieldDeclarationById } from './getFieldDeclarationById';

/**
 * Функция, выставляющая в fieldState.valuesByKey значение value поля по ключу unionKeyValue
 */
export function setValueByKey({
    form,
    unionFieldId,
    unionKeyValue,
    value,
}: {
    form: WritableDraft<FormSliceState['forms'][string]>;
    unionFieldId: string;
    unionKeyValue: Primitive;
    value: unknown;
}) {
    const fieldState = form.fieldsState[unionFieldId];

    if (!fieldState || !('valuesByKey' in fieldState)) {
        return;
    }

    if (typeof unionKeyValue === undefined || typeof unionKeyValue === null) {
        return;
    }

    const unionKeyValueString = String(unionKeyValue);

    const previousValuesByKey = isDraft(fieldState.valuesByKey)
        ? original(fieldState.valuesByKey)
        : fieldState.valuesByKey;

    const newValuesByKey = {
        ...previousValuesByKey,
        [unionKeyValueString]: value,
    };

    if (!deepEqual(newValuesByKey, previousValuesByKey)) {
        fieldState.valuesByKey = castDraft(newValuesByKey);
    }
}

/** Устанавливает сохраненные значения объектов у всех родительских union полей */
export function setParentsValueByKey(props: {
    /** Значение формы из слайса */
    form: WritableDraft<FormSliceState['forms'][string]>;
    /** Поля формы из декларации */
    formFields: BaseFields;
    /** ID поля */
    fieldId: string;
}) {
    const { form, formFields, fieldId } = props;

    let currentFieldId: string | undefined = fieldId;

    while (currentFieldId) {
        const fieldState:
            | DistributiveOmit<FieldState<unknown, BaseValidationError>, CalculatedFieldStateProperties>
            | undefined = form.fieldsState[currentFieldId];

        if (fieldState && 'valuesByKey' in fieldState) {
            const value = isDraft(form.value) ? current(form.value) : form.value;
            const path = getFieldPathById(() => form.fieldsState, currentFieldId);
            const savingValue = get(value, path);

            const fieldDeclaration = getFieldDeclarationById({
                fieldsState: form.fieldsState,
                formFields,
                fieldId: currentFieldId,
                // value с корня формы
                formValue: value,
            });

            if (fieldDeclaration?.type !== 'union') {
                console.warn(
                    new UnexpectedError(`По пути декларации не найдена декларация или не является union-полем`, {
                        cause: null,
                        additional: {
                            fieldDeclaration: JSON.stringify(fieldDeclaration),
                            currentFieldId,
                        },
                    })
                );

                continue;
            }

            const unionKey = fieldDeclaration.key;

            if (!isObject(savingValue) || unionKey === undefined) {
                continue;
            }

            const unionKeyValue = savingValue[unionKey];

            if (unionKeyValue === undefined || !isPrimitive(unionKeyValue)) {
                continue;
            }

            setValueByKey({
                form,
                unionFieldId: currentFieldId,
                unionKeyValue,
                value: savingValue,
            });
        }

        currentFieldId = fieldState?.parentId;
    }
}
